import React, { useState, useEffect, useCallback } from 'react';
import { tagsApi, TagsByCategory } from '../services/api';
import { Tag, TagCategory, CreateTagData } from '../types';
import './TagManager.css';

interface TagManagerProps {
  onTagsChange?: () => void;
}

interface TagWithCount extends Tag {
  bookCount: number;
}

interface CategoryWithTagCounts {
  category: TagCategory;
  tags: TagWithCount[];
}

interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

function ConfirmModal({
  isOpen,
  title,
  message,
  onConfirm,
  onCancel,
  isLoading = false,
}: ConfirmModalProps): React.ReactElement | null {
  if (!isOpen) return null;

  return (
    <div className="tag-manager__modal-overlay" onClick={onCancel}>
      <div
        className="tag-manager__modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        <h3 id="modal-title" className="tag-manager__modal-title">
          {title}
        </h3>
        <p className="tag-manager__modal-message">{message}</p>
        <div className="tag-manager__modal-actions">
          <button
            type="button"
            className="tag-manager__button tag-manager__button--secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="tag-manager__button tag-manager__button--danger"
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Removendo...' : 'Confirmar Remoção'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface MoveTagModalProps {
  isOpen: boolean;
  tag: TagWithCount | null;
  categories: TagCategory[];
  currentCategoryId: number;
  onConfirm: (newCategoryId: number) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

function MoveTagModal({
  isOpen,
  tag,
  categories,
  currentCategoryId,
  onConfirm,
  onCancel,
  isLoading = false,
}: MoveTagModalProps): React.ReactElement | null {
  const [selectedCategoryId, setSelectedCategoryId] = useState<number>(currentCategoryId);

  useEffect(() => {
    setSelectedCategoryId(currentCategoryId);
  }, [currentCategoryId, isOpen]);

  if (!isOpen || !tag) return null;

  const availableCategories = categories.filter(c => c.id !== currentCategoryId);

  return (
    <div className="tag-manager__modal-overlay" onClick={onCancel}>
      <div
        className="tag-manager__modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="move-modal-title"
      >
        <h3 id="move-modal-title" className="tag-manager__modal-title">
          Mover Tag
        </h3>
        <p className="tag-manager__modal-message">
          Mover a tag "<strong>{tag.name}</strong>" para outra categoria:
        </p>
        <div className="tag-manager__form-field" style={{ marginBottom: '1rem' }}>
          <select
            className="tag-manager__select"
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
            disabled={isLoading}
          >
            <option value={currentCategoryId} disabled>Selecione a nova categoria</option>
            {availableCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.displayName}
              </option>
            ))}
          </select>
        </div>
        <div className="tag-manager__modal-actions">
          <button
            type="button"
            className="tag-manager__button tag-manager__button--secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="tag-manager__button tag-manager__button--primary"
            onClick={() => onConfirm(selectedCategoryId)}
            disabled={isLoading || selectedCategoryId === currentCategoryId}
          >
            {isLoading ? 'Movendo...' : 'Mover Tag'}
          </button>
        </div>
      </div>
    </div>
  );
}


export function TagManager({ onTagsChange }: TagManagerProps): React.ReactElement {
  const [categoriesWithTags, setCategoriesWithTags] = useState<CategoryWithTagCounts[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Estado do formulário de nova tag
  const [newTagName, setNewTagName] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  
  // Estado do modal de confirmação de remoção
  const [tagToDelete, setTagToDelete] = useState<TagWithCount | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Estado do modal de mover tag
  const [tagToMove, setTagToMove] = useState<TagWithCount | null>(null);
  const [tagToMoveCategoryId, setTagToMoveCategoryId] = useState<number>(0);
  const [isMoving, setIsMoving] = useState(false);
  
  // Filtro de tags
  const [tagSearchFilter, setTagSearchFilter] = useState('');

  const loadTagsWithCounts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const tagsByCategory = await tagsApi.getAllByCategory();
      
      // A contagem de livros já vem na resposta da API
      const categoriesWithCounts: CategoryWithTagCounts[] = tagsByCategory.map((categoryData: TagsByCategory & { tags: Array<Tag & { bookCount?: number }> }) => ({
        category: categoryData.category,
        tags: categoryData.tags.map((tag) => ({
          ...tag,
          bookCount: (tag as any).bookCount || 0
        }))
      }));
      
      setCategoriesWithTags(categoriesWithCounts);
      
      // Define a primeira categoria como selecionada se nenhuma estiver selecionada
      if (!selectedCategoryId && categoriesWithCounts.length > 0) {
        setSelectedCategoryId(categoriesWithCounts[0].category.id);
      }
    } catch (err) {
      const errorMessage =
        (err as { message?: string })?.message || 'Erro ao carregar tags. Tente novamente.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategoryId]);

  useEffect(() => {
    loadTagsWithCounts();
  }, [loadTagsWithCounts]);

  // Limpa mensagem de sucesso após 3 segundos
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (!newTagName.trim()) {
      setFormError('O nome da tag é obrigatório');
      return;
    }

    if (!selectedCategoryId) {
      setFormError('Selecione uma categoria');
      return;
    }

    setIsCreating(true);
    try {
      const tagData: CreateTagData = {
        categoryId: selectedCategoryId,
        name: newTagName.trim(),
      };
      
      await tagsApi.create(tagData);
      setNewTagName('');
      setSuccessMessage('Tag criada com sucesso!');
      await loadTagsWithCounts();
      
      if (onTagsChange) {
        onTagsChange();
      }
    } catch (err) {
      const errorMessage =
        (err as { message?: string })?.message || 'Erro ao criar tag. Tente novamente.';
      setFormError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteClick = (tag: TagWithCount) => {
    setTagToDelete(tag);
  };

  const handleCancelDelete = () => {
    setTagToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!tagToDelete) return;

    setIsDeleting(true);
    try {
      await tagsApi.delete(tagToDelete.id);
      setSuccessMessage(`Tag "${tagToDelete.name}" removida com sucesso!`);
      setTagToDelete(null);
      await loadTagsWithCounts();
      
      if (onTagsChange) {
        onTagsChange();
      }
    } catch (err) {
      const errorMessage =
        (err as { message?: string })?.message || 'Erro ao remover tag. Tente novamente.';
      setError(errorMessage);
      setTagToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleMoveClick = (tag: TagWithCount, categoryId: number) => {
    setTagToMove(tag);
    setTagToMoveCategoryId(categoryId);
  };

  const handleCancelMove = () => {
    setTagToMove(null);
    setTagToMoveCategoryId(0);
  };

  const handleConfirmMove = async (newCategoryId: number) => {
    if (!tagToMove) return;

    setIsMoving(true);
    try {
      await tagsApi.moveToCategory(tagToMove.id, newCategoryId);
      setSuccessMessage(`Tag "${tagToMove.name}" movida com sucesso!`);
      setTagToMove(null);
      setTagToMoveCategoryId(0);
      await loadTagsWithCounts();
      
      if (onTagsChange) {
        onTagsChange();
      }
    } catch (err) {
      const errorMessage =
        (err as { message?: string })?.message || 'Erro ao mover tag. Tente novamente.';
      setError(errorMessage);
      setTagToMove(null);
      setTagToMoveCategoryId(0);
    } finally {
      setIsMoving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="tag-manager">
        <div className="tag-manager__loading">
          <div className="tag-manager__spinner" />
          <span>Carregando tags...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="tag-manager">
      <div className="tag-manager__header">
        <h2 className="tag-manager__title">Gerenciamento de Tags</h2>
        <button
          type="button"
          className="tag-manager__button tag-manager__button--secondary"
          onClick={loadTagsWithCounts}
          disabled={isLoading}
        >
          Atualizar
        </button>
      </div>

      {error && (
        <div className="tag-manager__message tag-manager__message--error">
          {error}
          <button
            type="button"
            className="tag-manager__message-close"
            onClick={() => setError(null)}
            aria-label="Fechar mensagem"
          >
            ✕
          </button>
        </div>
      )}

      {successMessage && (
        <div className="tag-manager__message tag-manager__message--success">
          {successMessage}
          <button
            type="button"
            className="tag-manager__message-close"
            onClick={() => setSuccessMessage(null)}
            aria-label="Fechar mensagem"
          >
            ✕
          </button>
        </div>
      )}

      {/* Formulário para criar nova tag */}
      <div className="tag-manager__form-section">
        <h3 className="tag-manager__section-title">Criar Nova Tag</h3>
        <form className="tag-manager__form" onSubmit={handleCreateTag}>
          <div className="tag-manager__form-row">
            <div className="tag-manager__form-field">
              <label htmlFor="category" className="tag-manager__label">
                Categoria
              </label>
              <select
                id="category"
                className="tag-manager__select"
                value={selectedCategoryId || ''}
                onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
                disabled={isCreating}
              >
                <option value="">Selecione uma categoria</option>
                {categoriesWithTags.map((cat) => (
                  <option key={cat.category.id} value={cat.category.id}>
                    {cat.category.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div className="tag-manager__form-field tag-manager__form-field--grow">
              <label htmlFor="tagName" className="tag-manager__label">
                Nome da Tag
              </label>
              <input
                type="text"
                id="tagName"
                className="tag-manager__input"
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder="Digite o nome da tag"
                disabled={isCreating}
              />
            </div>
            <button
              type="submit"
              className="tag-manager__button tag-manager__button--primary tag-manager__form-submit"
              disabled={isCreating || !newTagName.trim() || !selectedCategoryId}
            >
              {isCreating ? 'Criando...' : 'Criar Tag'}
            </button>
          </div>
          {formError && (
            <span className="tag-manager__form-error">{formError}</span>
          )}
        </form>
      </div>

      {/* Lista de tags por categoria */}
      <div className="tag-manager__categories">
        <div className="tag-manager__filter-section">
          <input
            type="text"
            placeholder="Filtrar tags..."
            value={tagSearchFilter}
            onChange={(e) => setTagSearchFilter(e.target.value)}
            className="tag-manager__filter-input"
          />
        </div>
        {categoriesWithTags.map((categoryData) => {
          const filteredTags = tagSearchFilter.trim() 
            ? categoryData.tags.filter(tag => 
                tag.name.toLowerCase().includes(tagSearchFilter.toLowerCase())
              )
            : categoryData.tags;
          
          if (tagSearchFilter.trim() && filteredTags.length === 0) return null;
          
          return (
          <div key={categoryData.category.id} className="tag-manager__category">
            <h3 className="tag-manager__category-title">
              {categoryData.category.displayName}
              <span className="tag-manager__category-count">
                ({filteredTags.length} {filteredTags.length === 1 ? 'tag' : 'tags'})
              </span>
            </h3>
            {filteredTags.length === 0 ? (
              <p className="tag-manager__empty-category">
                Nenhuma tag nesta categoria
              </p>
            ) : (
              <div className="tag-manager__tag-list">
                {filteredTags.map((tag) => (
                  <div key={tag.id} className="tag-manager__tag-item">
                    <div className="tag-manager__tag-info">
                      <span className="tag-manager__tag-name">{tag.name}</span>
                      <span className="tag-manager__tag-book-count">
                        {tag.bookCount} {tag.bookCount === 1 ? 'projeto' : 'projetos'}
                      </span>
                    </div>
                    <div className="tag-manager__tag-actions">
                      <button
                        type="button"
                        className="tag-manager__tag-move"
                        onClick={() => handleMoveClick(tag, categoryData.category.id)}
                        title="Mover para outra categoria"
                        aria-label={`Mover tag ${tag.name} para outra categoria`}
                      >
                        ↔️
                      </button>
                      <button
                        type="button"
                        className="tag-manager__tag-delete"
                        onClick={() => handleDeleteClick(tag)}
                        title="Remover tag"
                        aria-label={`Remover tag ${tag.name}`}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
        })}
      </div>

      <ConfirmModal
        isOpen={tagToDelete !== null}
        title="Confirmar Remoção"
        message={
          tagToDelete
            ? `Tem certeza que deseja remover a tag "${tagToDelete.name}"?${
                tagToDelete.bookCount > 0
                  ? ` Esta tag está sendo usada por ${tagToDelete.bookCount} ${
                      tagToDelete.bookCount === 1 ? 'projeto' : 'projetos'
                    } e será removida de todos eles.`
                  : ''
              }`
            : ''
        }
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
      />

      <MoveTagModal
        isOpen={tagToMove !== null}
        tag={tagToMove}
        categories={categoriesWithTags.map(c => c.category)}
        currentCategoryId={tagToMoveCategoryId}
        onConfirm={handleConfirmMove}
        onCancel={handleCancelMove}
        isLoading={isMoving}
      />
    </div>
  );
}

export default TagManager;
